import { NgIf, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, shareReplay, take, takeUntil } from 'rxjs/operators';
import { Breadcrumb } from 'src/app/models/breadcrumb.model';
import { CatalogItem } from 'src/app/models/catalog-item.model';
import { catalogCategoriesRequest, catalogItemRequest, catalogItemRequestSuccess, setSelectedCatalogCategory } from 'src/app/store/actions';
import { selectSelectedCatalogCategory } from 'src/app/store/selectors';

import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { CatalogCategoriesComponent } from '../catalog-categories/catalog-categories.component';
import { CatalogSubCategoriesComponent } from '../catalog-subcategories/catalog-subcategories.component';

@Component({
  selector: 'app-catalog-category-selector',
  templateUrl: './catalog-category-selector.component.html',
  styleUrl: './catalog-category-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, BreadcrumbComponent, CatalogCategoriesComponent, CatalogSubCategoriesComponent, AsyncPipe, AgrI18nSharedModule],
})
export class CatalogCategorySelectorComponent implements OnInit, OnDestroy {
  @Input() showBreadcrumb = true;

  breadcrumbs$: Observable<Breadcrumb[]>;
  showMainCategory$: Observable<boolean>;
  selectedCatalogCategory$: Observable<CatalogItem> = this.store.select(selectSelectedCatalogCategory);

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<RootState>,
    private readonly actions$: Actions,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit() {
    this.breadcrumbs$ = this.activatedRoute.queryParams.pipe(
      map((params: Params) => this.getCategoriesFromParams(params)),
      shareReplay(1),
    );

    combineLatest([this.selectedCatalogCategory$, this.breadcrumbs$])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([selectedCatalogCategory, breadcrumbs]) => {
        const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
        if (!breadcrumbs.length && selectedCatalogCategory) {
          this.store.dispatch(catalogCategoriesRequest({ isRoot: true }));
        } else if (breadcrumbs.length && selectedCatalogCategory?.entryId !== lastBreadcrumb.id) {
          this.setCategoryStoreState(lastBreadcrumb.id);
        }
      });

    this.showMainCategory$ = this.breadcrumbs$.pipe(map((breadcrumbs) => breadcrumbs.length === 0));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigateToMainCategory() {
    this.breadcrumbs$.pipe(take(1), takeUntil(this.destroy$)).subscribe((breadcrumbs) => {
      if (breadcrumbs?.length) {
        const queryParams = {
          ...this.activatedRoute.snapshot.queryParams,
        };
        delete queryParams.categories;
        this.router.navigate(['shop'], { queryParams });

        this.store.dispatch(
          setSelectedCatalogCategory({
            selectedCatalogCategory: undefined,
          }),
        );
      }
    });
  }

  navigateToCategoryByIndex(categoryIndex: number) {
    this.breadcrumbs$.pipe(take(1), takeUntil(this.destroy$)).subscribe((breadcrumbs) => {
      const isNotLastIndex = breadcrumbs.length && breadcrumbs.length - 1 !== categoryIndex;

      if (isNotLastIndex) {
        const categories = breadcrumbs.slice(0, categoryIndex + 1);
        const queryParams = {
          ...this.activatedRoute.snapshot.queryParams,
          categories: JSON.stringify(categories),
        };
        this.router.navigate(['shop'], { queryParams });

        this.setCategoryStoreState(categories[categories.length - 1].id);
      }
    });
  }

  navigateToCategory(category: CatalogItem, replaceLastBreadcrumb = false) {
    this.activatedRoute.queryParams.pipe(take(1), takeUntil(this.destroy$)).subscribe((params: Params) => {
      const categories = this.getCategoriesFromParams(params);
      if (replaceLastBreadcrumb) {
        // When we are switching within a category, the last breadcrumb should be removed
        categories.pop();
      }
      categories.push({ id: category.entryId, title: category.description });

      const queryParams = {
        ...params,
        categories: JSON.stringify(categories),
      };
      this.router.navigate(['shop'], { queryParams });
    });

    this.setCategoryStoreState(category);
  }

  private setCategoryStoreState(category: CatalogItem | string) {
    const id = typeof category === 'object' ? category.entryId : category;

    let getCategory$: Observable<CatalogItem>;
    if (typeof category === 'object') {
      getCategory$ = of(category);
    } else {
      getCategory$ = this.actions$.pipe(
        ofType(catalogItemRequestSuccess),
        map((result) => result.catalogItem),
        filter((catalogItem) => catalogItem.entryId === id),
      );
      this.store.dispatch(catalogItemRequest({ id }));
    }

    getCategory$.pipe(take(1), takeUntil(this.destroy$)).subscribe((newCategory) => {
      this.store.dispatch(
        setSelectedCatalogCategory({
          selectedCatalogCategory: newCategory,
        }),
      );
    });
  }

  private getCategoriesFromParams(params: Params): Breadcrumb[] {
    let categories = [];

    if (params.categories) {
      try {
        categories = JSON.parse(params.categories);
      } catch (ex) {
        console.error('Unable to read categories from query parameters', ex);
      }
    }
    return categories;
  }
}
